<template>
  <div>
    <v-menu
      @update:return-value="$emit('close')"
      :value="show"
      :nudge-width="200"
      offset-x
      top
      allow-overflow
      max-width="300px"
    >
      <template v-slot:activator="{ on }">
        <slot name="activator">
          <v-btn v-on="on" color="brand">
            <v-icon small color="white" class="mr-2">{{
              icons.mdiShareVariant
            }}</v-icon>
            <span class="white--text">{{ $t('share') }}</span>
          </v-btn>
        </slot>
      </template>

      <v-card>
        <v-list>
          <v-subheader>{{ $t('copyToClipBoard') }}</v-subheader>

          <v-list-item @click="copyToClipboard(currentRoute)">
            <v-list-item-avatar size="32">
              <v-icon color="primary">{{ icons.mdiFileMultiple }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-title
              class="pa-3 inputBg"
              style="border-radius: 3px;"
              >{{ currentRoute }}</v-list-item-title
            >
          </v-list-item>

          <v-tooltip right color="black">
            <template v-slot:activator="{ on }">
              <v-list-item
                v-if="entity === 'PROJECT'"
                v-on="on"
                @click="copyToClipboard(directRegistrarionRoute)"
              >
                <v-list-item-avatar size="32" color="primary">
                  <v-icon color="white" size="20">{{
                    icons.mdiAccountMultiplePlus
                  }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-title
                  class="pa-3 inputBg"
                  style="border-radius: 3px;"
                  >{{ directRegistrarionRoute }}</v-list-item-title
                >
              </v-list-item>
            </template>
            <span>{{ $t('directRegistrationLink') }}</span>
          </v-tooltip>

          <v-divider class="my-2"></v-divider>

          <v-list-item v-if="entity && entityId" @click="qrDialog = true">
            <div class="d-flex align-center">
              <div style="width:55px;">
                <v-avatar size="28" class="ma-0 pa-0">
                  <v-icon color="primary">{{
                    icons.mdiImageFilterCenterFocus
                  }}</v-icon>
                </v-avatar>
              </div>
              <span class="primary--text">{{ $t('shareQr') }}</span>
            </div>
          </v-list-item>

          <v-list-item
            v-for="social in shareableLinks"
            class="hover-bg"
            :key="social.network"
          >
            <share-network
              :network="social.network"
              :url="social.url"
              :title="social.title"
              :description="social.description"
              :hashtags="social.hashtags"
            >
              <div class="d-flex align-center">
                <div style="width:55px;">
                  <v-avatar
                    v-if="social.avatarColor"
                    size="28"
                    :color="social.avatarColor"
                    class="ma-0 pa-0"
                  >
                    <v-icon size="20" :color="social.iconColor">{{
                      social.icon
                    }}</v-icon>
                  </v-avatar>
                  <v-icon v-else size="31" :color="social.iconColor">
                    {{ social.icon }}
                  </v-icon>
                </div>
                <span class="primary--text">{{ social.text }}</span>
              </div>
            </share-network>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <qr-code-dialog
      v-if="entity && entityId && qrDialog"
      @close="qrDialog = false"
      :show="qrDialog"
      :uuid="uuid"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  mdiTwitter,
  mdiFacebook,
  mdiLinkedin,
  mdiWhatsapp,
  mdiShareVariant,
  mdiFileMultiple,
  mdiAccountMultiplePlus,
  mdiImageFilterCenterFocus
} from '@mdi/js';
import Swal from 'sweetalert2';

import { qr } from '@/utilities/api';
import {
  VUE_APP_API_URL,
  QR_APPLY_PROJECT_ID_ROUTES
} from '@/utilities/constants.js';

const QrCodeDialog = () =>
  import('@/components/base_components/QrCodeDialog/QrCodeDialog.vue');

export default {
  components: {
    QrCodeDialog
  },

  props: {
    show: {
      type: Boolean,
      required: false,
      default: false
    },

    entity: {
      type: String,
      required: false
    },

    entityId: {
      type: String,
      required: false
    },

    description: {
      type: String,
      default: () => ''
    },

    title: {
      type: String,
      default: () => ''
    },

    projectHashtags: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      qrDialog: false,
      uuid: ''
    };
  },

  methods: {
    createPayload() {
      const payload = {
        entity: this.entity,
        entityId: this.entityId
      };

      // if creating for document
      if (this.entity === 'DOCUMENT') {
        // if project id route
        if (
          QR_APPLY_PROJECT_ID_ROUTES.includes(this.$route.name) &&
          this.$route?.params?.id
        ) {
          payload['projectId'] = this.$route?.params?.id;
          //if it inclucdes projectId query
        } else if (this.$route?.query?.projectId) {
          payload['projectId'] = this.$route?.query?.projectId;
        }
      }

      return payload;
    },

    async generateQrCode() {
      try {
        const payload = this.createPayload();

        this.createLoading = true;
        const createRes = await qr.create(payload);
        this.uuid = createRes.data.uuid;
        this.createLoading = false;
      } catch (e) {
        this.createLoading = false;
        this.createError = true;
        throw new Error('QR Code not successfully created');
      }
    },

    getSocialLink(social) {
      const url = encodeURIComponent(window.location.href);

      switch (social) {
        case 'twitter':
          window.open(`https://twitter.com/share?url=${url}`, '_blank');
          break;
        case 'linkedin':
          window.open(
            `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
            '_blank'
          );
          break;

        case 'facebook':
          window.open(
            `https://www.facebook.com/sharer/sharer.php?=${url}`,
            '_blank'
          );
          break;
      }
    },

    async copyToClipboard(route) {
      try {
        await navigator.clipboard.writeText(route);
        Swal.fire({
          title: this.$t('success'),
          text: this.$t('urlCopiedToClipboard'),
          type: 'success',
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          timer: 3000
        });
      } catch (e) {
        return e;
      }
    }
  },

  computed: {
    ...mapState({
      brand: state => state.brand.brand,
      defaultLanguage: state => state.defaultLanguage
    }),

    currentRoute() {
      if (!this.uuid) return '';
      return `${window.location.origin}/qrcode/${this.uuid}`;
    },

    directRegistrarionRoute() {
      if (this.entity !== 'PROJECT') return '';

      return `${window.location.origin}/direct-registration/${this.entityId}`;
    },

    icons: () => ({
      mdiTwitter,
      mdiFacebook,
      mdiLinkedin,
      mdiShareVariant,
      mdiFileMultiple,
      mdiAccountMultiplePlus,
      mdiImageFilterCenterFocus
    }),

    currUrl() {
      if (this.entity === 'DOCUMENT') {
        return `${VUE_APP_API_URL}/share/showcase/${this.entityId}?language=${this.defaultLanguage}`;
      } else if (this.entity === 'ITINERARY') {
        return `${VUE_APP_API_URL}/share/itinerary/${this.entityId}?language=${this.defaultLanguage}`;
      } else if (this.entity === 'PROFILE') {
        return `${VUE_APP_API_URL}/share/profile/${this.entityId}?language=${this.defaultLanguage}`;
      }
      return `${VUE_APP_API_URL}/share/view-projects/${this.entityId}?language=${this.defaultLanguage}`;
    },

    brandHashtags() {
      return this.brand?.hashtags;
    },

    hashtags() {
      return [...this.brandHashtags, ...this.projectHashtags].join(',');
    },

    whatsAppHashTags() {
      return [...this.brandHashtags, ...this.projectHashtags].join(',#');
    },

    shareableLinks() {
      const common = {
        url: this.currUrl,
        title: this.title,
        description: this.description,
        hashtags: this.hashtags
      };

      const twitter = {
        ...common,
        network: 'twitter',
        avatarColor: '#1e95de',
        iconColor: '#ffffff',
        text: this.$t('shareOnTwitter'),
        icon: mdiTwitter
      };

      const facebook = {
        ...common,
        network: 'facebook',
        text: this.$t('shareOnFacebook'),
        iconColor: '#4285f4',
        icon: mdiFacebook,
        hashtags: this.projectHashtags[0]
      };

      const linkedIn = {
        ...common,
        network: 'LinkedIn',
        text: this.$t('shareOnLinkedIn'),
        iconColor: '#0577b5',
        icon: mdiLinkedin,
        hashtags: this.hashtags
      };

      const whatsApp = {
        ...common,
        network: 'WhatsApp',
        text: this.$t('shareOnWhatsApp'),
        description: this.description + ' #' + this.whatsAppHashTags,
        iconColor: '#24cc62',
        icon: mdiWhatsapp,
        hashtags: this.whatsAppHashTags
      };

      return [twitter, facebook, linkedIn, whatsApp];
    }
  },

  watch: {
    show() {
      if (this.show && this.uuid === '') {
        this.generateQrCode();
      }
    }
  }
};
</script>

<style scoped>
.hover-bg {
  transition: 0.4s;
}

.hover-bg:hover {
  background: whitesmoke;
}
</style>
